<template>
  <div class="tw-my-2">
    <p class="tw-font-bold tw-mb-1">Productie laatste 7 dagen</p>
    <p v-if="isResolving || isLoading" class="tw-text-gray-500">Laden...</p>
    <p v-else-if="!serial" class="tw-text-gray-500">Geen meter gevonden op dit adres</p>
    <p v-else-if="isError" class="tw-text-gray-500">Productie ophalen mislukt: {{ errorMessage }}</p>
    <div v-else class="tw-h-40">
      <Bar :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
// Daily production of an address over the last 7 complete days (until yesterday), from the same daily
// aggregates the polling page uses (scope address: every meter on the address summed). The meter is
// the meter the tickets location has for the address (the one that communicated last), else the
// smartmeter of the installation ticket.
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Tooltip, BarElement, CategoryScale, LinearScale, BarController } from 'chart.js'
import { addDays, format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { toLocationId, pickMeter } from '@/util/meterLinks'

ChartJS.register(Tooltip, BarElement, CategoryScale, LinearScale, BarController)

const props = defineProps({
  serialNumber: { type: String, default: null },
  postalCode: { type: String, default: null },
  houseNumber: { type: [String, Number], default: null },
})

const locationId = computed(() => toLocationId(props.postalCode, props.houseNumber))
const { data: locationSerial, isInitialLoading: isResolvingLocation } = useQuery({
  queryKey: computed(() => ['callCentreLocationMeter', locationId.value]),
  queryFn: async () => {
    const response = await fetchData(`/tickets/api/v1/locations/${encodeURIComponent(locationId.value)}`)
    return pickMeter(response?.data?.eleenas)
  },
  enabled: computed(() => !!locationId.value),
  refetchOnWindowFocus: false,
  retry: false,
})
// a replaced meter can still be on an old ticket: the location's current meter wins
const serial = computed(() => locationSerial.value || props.serialNumber || null)
const isResolving = computed(() => !!locationId.value && isResolvingLocation.value)

const today = new Date()
// the last 7 complete days: the daily aggregates refuse a date_till after today ("You can not look
// into the future") and date_till is exclusive, so today itself is never in them
const days = Array.from({ length: 7 }, (_, i) => format(addDays(today, i - 7), 'yyyy-MM-dd'))

const { data: rows, isInitialLoading: isLoading, isError, error } = useQuery({
  queryKey: computed(() => ['callCentreLastWeek', serial.value]),
  queryFn: async () => {
    const params = new URLSearchParams({
      serial_number: serial.value,
      date_from: days[0],
      // exclusive: up to and including yesterday
      date_till: format(today, 'yyyy-MM-dd'),
      scope: 'address',
      limit: '31',
    })
    const result = await fetchData(`${READINGS_API_BASE}/daily-aggregates?${params}`)
    if (result?.status !== 'OK') throw new Error('daily-aggregates failed')
    return result.data
  },
  enabled: computed(() => !!serial.value),
  refetchOnWindowFocus: false,
  // a refused request (400) does not get better by retrying; show the reason right away
  retry: false,
})

// fetchData throws the JSON body of a failed request ({ status, message }), else an Error
const errorMessage = computed(() => error.value?.message || error.value?.detail || 'onbekende fout')

const chartData = computed(() => {
  const byDay = Object.fromEntries(days.map((d) => [d, 0]))
  ;(rows.value ?? []).forEach((row) => {
    const day = format(parseISO(row.date), 'yyyy-MM-dd')
    if (day in byDay) byDay[day] += Math.max(0, row.production ?? 0)
  })
  return {
    labels: days.map((d) => format(parseISO(d), 'EEE d', { locale: nl })),
    datasets: [
      {
        label: 'Productie (kWh)',
        data: days.map((d) => Math.round(byDay[d] / 100) / 10),
        backgroundColor: '#48c78e',
      },
    ],
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.y} kWh` } },
  },
  scales: { y: { beginAtZero: true, title: { display: true, text: 'kWh' } } },
}
</script>
